import { useEffect, useId, useRef, useState } from "react";
import { APP_LOCALE } from "@/config/currency";
import type { FilterField } from "@/config/filterFields";
import { useTextFilterModes } from "@/hooks/useFilterModes";
import { useFilterValues } from "@/hooks/useFilterValues";
import {
  MAX_FILTER_VALUES,
  MAX_PATTERN_LENGTH,
  MAX_TEXT_PATTERNS,
  addPattern,
  isNegativeMode,
  isTextMode,
  toggleValue,
  type FieldFilter,
} from "@/lib/filter-model";
import { cn } from "@/lib/utils";

const int = new Intl.NumberFormat(APP_LOCALE);
const MODE_LABEL: Record<FieldFilter["mode"], string> = {
  include: "Is",
  exclude: "Is not",
  contains: "Contains",
  not_contains: "Doesn't contain",
};
const PREVIEW_ROWS = 8;
// The key hints show after the first arrow key and stay for the visit (Р9b).
let keysUsed = false;
const rememberKeys = () => {
  keysUsed = true;
};

/**
 * One field's values, as the Columns list on Indexation looks: a flat list of
 * native checkboxes (a Radix checkbox per value made 12k domains freeze the
 * page). Values come from the server one field at a time, top ones by clicks
 * or the ones matching what is typed; picked ones are pinned on top when it
 * opens and on each new search. Nothing reaches the report until the editor
 * closes (FilterChip). A row's "only" makes it the one value and closes (Р2a);
 * with the search focused, ↑↓ walk the rows, Space ticks, Shift+Enter is
 * "only", Enter is Done (Р9b/c; docs/clicks-filter-buttons-variants.html).
 * "Contains" / "Doesn't contain" take pieces of text instead (owner, 03.11):
 * typed, Enter adds one; matched in any case, with the matching values shown
 * as a preview - offered once the server knows these modes (useTextFilterModes).
 */
export function ValueEditor({ field, draft, onDraft, onDone }: {
  field: FilterField;
  draft: FieldFilter;
  onDraft: (next: FieldFilter) => void;
  /** Closes the editor; `next` when a value's "only" applies at once. */
  onDone: (next?: FieldFilter) => void;
}) {
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const input = useRef<HTMLInputElement>(null);
  const modeButtons = useRef<Partial<Record<FieldFilter["mode"], HTMLButtonElement | null>>>({});
  // A fixed list (marks) has nothing to search in; elsewhere the text modes
  // wait for the server (an older one reads them as "is not").
  const textOn = useTextFilterModes() && !field.options;
  const modes: FieldFilter["mode"][] = textOn ? ["include", "exclude", "contains", "not_contains"] : ["include", "exclude"];
  const text = isTextMode(draft.mode);
  // The other kind's values while the editor is open: "is" -> "contains" -> "is"
  // gives the ticks back instead of turning values into pieces of text.
  const stash = useRef<Record<"exact" | "text", string[]>>({ exact: [], text: [] });
  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 250);
    return () => clearTimeout(t);
  }, [query]);
  // A fixed list (marks) is searched right here; every other field asks the
  // server - in the text modes for what is typed, else for the last piece.
  const previewFor = text ? debounced || draft.values[draft.values.length - 1] || "" : debounced;
  const server = useFilterValues(field.key, previewFor, !field.options && (!text || previewFor !== ""));
  const q = debounced.toLowerCase();
  const data = field.options
    ? field.options
        .filter((o) => !q || o.label.toLowerCase().includes(q) || o.value.includes(q))
        .map((o) => ({ value: o.value, clicks: o.count, lastSeen: null }))
    : server.data;
  const { isError, error, isFetching } = server;
  const isPending = !field.options && server.isPending;
  const shortList = Boolean(field.options && field.options.length <= 10);
  // Rows keep their place while the editor is open: a value ticked now just
  // shows its checkbox instead of jumping to the top under the cursor (owner,
  // 30.10). A new search re-pins, or what was ticked in the old results would
  // drop out of sight. The editor mounts on each open.
  const [pinned, setPinned] = useState(text ? [] : draft.values);
  const [pinnedFor, setPinnedFor] = useState(debounced);
  const [active, setActive] = useState(-1);
  const [hints, setHints] = useState(keysUsed);
  if (pinnedFor !== debounced) {
    setPinnedFor(debounced);
    setPinned(text ? [] : draft.values);
    setActive(-1);
  }
  const setMode = (mode: FieldFilter["mode"]) => {
    const from = text ? "text" : "exact";
    const to = isTextMode(mode) ? "text" : "exact";
    if (from === to) return onDraft({ ...draft, mode });
    stash.current[from] = draft.values;
    const values = stash.current[to];
    if (to === "exact") setPinned(values);
    setActive(-1);
    onDraft({ mode, values });
  };
  // Done takes what is typed in a text mode too: nobody expects to press Enter first.
  const finish = () => {
    if (text && query.trim()) {
      const next = { ...draft, values: addPattern(draft.values, query) };
      onDraft(next);
      onDone(next);
    } else onDone();
  };
  const found = (data ?? []).filter((v) => !pinned.includes(v.value));
  const full = draft.values.length >= MAX_FILTER_VALUES;
  const toggle = (value: string) => onDraft({ ...draft, values: toggleValue(draft.values, value) });
  const only = (value: string) => {
    const next = { ...draft, values: [value] };
    onDraft(next);
    onDone(next);
  };
  // The rows in the order they render, for the arrow keys.
  const order = [...pinned, ...(isError || isPending ? [] : found.map((v) => v.value))];
  const activeValue = active >= 0 ? order[active] : undefined;
  const uid = useId();
  const rowId = (i: number) => `${uid}-row-${i}`;
  useEffect(() => {
    if (active >= 0) document.getElementById(`${uid}-row-${active}`)?.scrollIntoView({ block: "nearest" });
  }, [active, uid]);

  return (
    <div
      className="space-y-2"
      // Enter is Done - except on a button, which Enter presses (Is not, Clear);
      // Shift+Enter on the row the arrows are on is its "only".
      onKeyDown={(e) => {
        if (e.key !== "Enter" || e.target instanceof HTMLButtonElement) return;
        e.preventDefault();
        if (e.shiftKey && activeValue !== undefined && !text) only(activeValue);
        else finish();
      }}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium">{field.label}</span>
        <span className="text-xs text-muted-foreground tabular-nums">
          {text
            ? draft.values.length ? `${draft.values.length} added` : "nothing added"
            : draft.values.length ? `${draft.values.length} selected` : "none selected"}
        </span>
      </div>

      <div
        role="radiogroup"
        aria-label="Match"
        className="inline-flex overflow-hidden rounded-md border border-border text-xs"
        // A radio group: one tab stop, the arrows switch (WAI-ARIA radio pattern).
        onKeyDown={(e) => {
          if (!["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(e.key)) return;
          e.preventDefault();
          const step = e.key === "ArrowLeft" || e.key === "ArrowUp" ? -1 : 1;
          const mode = modes[(modes.indexOf(draft.mode) + step + modes.length) % modes.length];
          setMode(mode);
          modeButtons.current[mode]?.focus();
        }}
      >
        {modes.map((mode) => (
          <button
            key={mode}
            ref={(el) => {
              modeButtons.current[mode] = el;
            }}
            type="button"
            role="radio"
            aria-checked={draft.mode === mode}
            tabIndex={draft.mode === mode ? 0 : -1}
            onClick={() => setMode(mode)}
            className={cn(
              "cursor-pointer px-2.5 py-1 whitespace-nowrap",
              draft.mode === mode
                ? isNegativeMode(mode) ? "bg-destructive text-white" : "bg-primary text-primary-foreground"
                : "hover:bg-muted",
            )}
          >
            {MODE_LABEL[mode]}
          </button>
        ))}
      </div>

      {text ? (
        <TextPatterns
          label={field.label}
          mode={draft.mode}
          patterns={draft.values}
          query={query}
          onQuery={setQuery}
          onPatterns={(values) => onDraft({ ...draft, values })}
          preview={(server.data ?? []).slice(0, PREVIEW_ROWS)}
          more={Math.max(0, (server.data?.length ?? 0) - PREVIEW_ROWS)}
          previewFor={previewFor}
          loading={server.isPending && previewFor !== ""}
          failed={isError}
        />
      ) : null}

      {shortList || text ? null : (
        <input
          ref={input}
          autoFocus
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setActive(-1);
          }}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown" || e.key === "ArrowUp") {
              e.preventDefault();
              if (!order.length) return;
              const down = e.key === "ArrowDown";
              rememberKeys();
              setHints(true);
              setActive((i) => (down ? Math.min(i + 1, order.length - 1) : Math.max(i - 1, 0)));
            } else if (e.key === " " && activeValue !== undefined) {
              // Space ticks the row the arrows are on; typing again leaves the
              // rows (onChange), so a space in a search still types.
              e.preventDefault();
              if (!full || draft.values.includes(activeValue)) toggle(activeValue);
            }
          }}
          placeholder={`Search ${field.label.toLowerCase()}…`}
          aria-label={`Search ${field.label}`}
          aria-controls={`${uid}-list`}
          aria-activedescendant={activeValue !== undefined ? rowId(active) : undefined}
          className="h-8 w-full rounded-md border border-border bg-transparent px-2 text-sm outline-none focus-visible:ring-1 focus-visible:ring-ring"
        />
      )}

      <div id={`${uid}-list`} className={cn("max-h-64 space-y-0.5 overflow-y-auto", text && "hidden")} role="group" aria-label={`${field.label} values`}>
        {pinned.length ? (
          <>
            <p className="px-1 pt-1 text-[11px] font-medium tracking-wide text-muted-foreground uppercase">Selected</p>
            {pinned.map((v, i) => (
              <ValueRow
                key={`s:${v}`}
                id={rowId(i)}
                active={active === i}
                onOnly={() => only(v)}
                value={v}
                label={field.valueLabel?.(v)}
                checked={draft.values.includes(v)}
                disabled={full}
                onToggle={() => toggle(v)}
              />
            ))}
          </>
        ) : null}
        <p className="px-1 pt-1 text-[11px] font-medium tracking-wide text-muted-foreground uppercase">
          {field.options ? "Values" : debounced ? "Matches · 30 days, all traffic" : "Top by clicks · 30 days, all traffic"}
        </p>
        {isError ? (
          <p className="px-1 py-1 text-xs text-destructive dark:text-red-400">Could not load values: {String(error?.message ?? "")}</p>
        ) : isPending ? (
          <p className="px-1 py-1 text-xs text-muted-foreground">Loading…</p>
        ) : found.length ? (
          found.map((v, i) => (
            <ValueRow
              key={v.value}
              id={rowId(pinned.length + i)}
              active={active === pinned.length + i}
              onOnly={() => only(v.value)}
              value={v.value}
              label={field.valueLabel?.(v.value)}
              clicks={v.clicks}
              checked={draft.values.includes(v.value)}
              disabled={full}
              onToggle={() => toggle(v.value)}
            />
          ))
        ) : (
          <p className="px-1 py-1 text-xs text-muted-foreground">{debounced ? "No such value" : "No values"}</p>
        )}
        {isFetching && !isPending ? <p className="px-1 text-[11px] text-muted-foreground">Updating…</p> : null}
      </div>

      {full && !text ? (
        <p className="text-xs text-muted-foreground">
          {MAX_FILTER_VALUES} values is the most a filter holds — for more, filter by a brand or a campaign instead.
        </p>
      ) : null}
      {draft.mode === "exclude" && !field.options ? (
        <p className="text-[11px] text-muted-foreground">Rows where {field.label.toLowerCase()} is empty stay, unless “Unknown” is picked.</p>
      ) : null}
      {text ? (
        <p className="text-[11px] text-muted-foreground">An empty {field.label.toLowerCase()} reads as “Unknown”.</p>
      ) : null}

      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border pt-2">
        <button
          type="button"
          onClick={() => {
            onDraft({ ...draft, values: [] });
            input.current?.focus();
          }}
          disabled={!draft.values.length}
          className="cursor-pointer text-xs text-muted-foreground hover:text-foreground disabled:cursor-default disabled:opacity-50"
        >
          Clear
        </button>
        {hints && !shortList && !text ? (
          <span className="text-[11px] text-muted-foreground [@media(pointer:coarse)]:hidden">↑↓ move · Space tick · ⇧↵ only</span>
        ) : null}
        <button
          type="button"
          onClick={finish}
          className="cursor-pointer rounded-md bg-cta px-3 py-1 text-xs font-medium text-cta-foreground hover:bg-cta/90"
        >
          Done <span aria-hidden className="opacity-70">↵</span>
        </button>
      </div>
    </div>
  );
}

function ValueRow({ id, active = false, value, label, clicks, checked = false, disabled = false, onToggle, onOnly }: {
  /** The checkbox's id - what the search's aria-activedescendant points at. */
  id: string;
  /** The row the arrow keys are on. */
  active?: boolean;
  value: string;
  /** How the value reads, when that is not the value itself (a mark). */
  label?: string;
  clicks?: number;
  checked?: boolean;
  disabled?: boolean;
  onToggle: () => void;
  onOnly: () => void;
}) {
  return (
    <label
      className={cn(
        "group/row flex items-center gap-2 rounded px-1 py-1 text-sm hover:bg-muted",
        active && "bg-muted ring-1 ring-ring",
        disabled && !checked ? "cursor-default opacity-50" : "cursor-pointer",
      )}
      title={label ?? value}
    >
      <input
        id={id}
        type="checkbox"
        className="size-3.5 shrink-0 cursor-pointer accent-primary"
        checked={checked}
        disabled={disabled && !checked}
        onChange={onToggle}
      />
      <span className="min-w-0 flex-1 truncate">{label ?? value}</span>
      {/* Always laid out, shown on hover, focus or the arrows (on touch: always),
          so the row never shifts. Not a tab stop: 50 of them would be; the keys
          have Shift+Enter. A press here is not the label's: no tick. */}
      <button
        type="button"
        tabIndex={-1}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onOnly();
        }}
        title="Only this value (Shift+Enter)"
        aria-label={`Only ${label ?? value}`}
        className={cn(
          "shrink-0 cursor-pointer text-xs text-accent-foreground hover:underline",
          active ? "opacity-100" : "opacity-0 group-hover/row:opacity-100 group-focus-within/row:opacity-100 [@media(pointer:coarse)]:opacity-100",
        )}
      >
        only
      </button>
      {clicks !== undefined ? <span className="text-xs text-muted-foreground tabular-nums">{int.format(clicks)}</span> : null}
    </label>
  );
}

/**
 * The pieces of text of a "contains": added with Enter, removed with × (or
 * Backspace in an empty box), and the values they match right now - the same
 * case-blind match the report makes (search_filter_values, clicks_filter_sql).
 */
function TextPatterns({ label, mode, patterns, query, onQuery, onPatterns, preview, more, previewFor, loading, failed }: {
  label: string;
  mode: FieldFilter["mode"];
  patterns: string[];
  query: string;
  onQuery: (q: string) => void;
  onPatterns: (next: string[]) => void;
  preview: { value: string; clicks?: number }[];
  more: number;
  previewFor: string;
  loading: boolean;
  failed: boolean;
}) {
  const full = patterns.length >= MAX_TEXT_PATTERNS;
  return (
    <div className="space-y-1.5">
      {patterns.length ? (
        <div className="flex flex-wrap gap-1">
          {patterns.map((p) => (
            <span key={p} className="inline-flex max-w-full items-center gap-0.5 rounded bg-accent py-0.5 pr-0.5 pl-1.5 text-xs text-accent-foreground">
              <span className="truncate">“{p}”</span>
              <button
                type="button"
                onClick={() => onPatterns(patterns.filter((x) => x !== p))}
                aria-label={`Remove “${p}”`}
                className="inline-flex size-4 cursor-pointer items-center justify-center rounded hover:bg-background"
              >
                ×
              </button>
            </span>
          ))}
        </div>
      ) : null}
      <input
        autoFocus
        value={query}
        maxLength={MAX_PATTERN_LENGTH}
        disabled={full}
        onChange={(e) => onQuery(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && query.trim()) {
            // Enter adds the piece; on an empty box it is Done (the editor's own handler).
            e.preventDefault();
            e.stopPropagation();
            onPatterns(addPattern(patterns, query));
            onQuery("");
          } else if (e.key === "Backspace" && !query && patterns.length) {
            onPatterns(patterns.slice(0, -1));
          }
        }}
        placeholder={full ? `${MAX_TEXT_PATTERNS} is the most` : "Type text, Enter adds it"}
        aria-label={`${label} ${mode === "contains" ? "contains" : "doesn't contain"}`}
        className="h-8 w-full rounded-md border border-border bg-transparent px-2 text-sm outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:opacity-50"
      />
      <p className="text-[11px] text-muted-foreground">
        {mode === "contains" ? "Values containing any of these" : "Leaves out values containing any of these"} - upper or lower case alike.
      </p>
      {previewFor ? (
        <div className="space-y-0.5" aria-live="polite">
          <p className="px-1 pt-1 text-[11px] font-medium tracking-wide text-muted-foreground uppercase">
            Values with “{previewFor}”
          </p>
          {failed ? (
            <p className="px-1 text-xs text-muted-foreground">Could not load the matching values</p>
          ) : loading ? (
            <p className="px-1 text-xs text-muted-foreground">Loading…</p>
          ) : preview.length ? (
            <>
              {preview.map((v) => (
                <div key={v.value} className="flex items-center gap-2 rounded px-1 py-0.5 text-sm" title={v.value}>
                  <span className="min-w-0 flex-1 truncate">{v.value}</span>
                  {v.clicks !== undefined ? <span className="text-xs text-muted-foreground tabular-nums">{int.format(v.clicks)}</span> : null}
                </div>
              ))}
              {more ? <p className="px-1 text-[11px] text-muted-foreground">and {more} more{more >= 42 ? "+" : ""}</p> : null}
            </>
          ) : (
            <p className="px-1 text-xs text-muted-foreground">No value contains it in the last 30 days</p>
          )}
        </div>
      ) : null}
    </div>
  );
}

